import React, { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from '@emotion/styled';

import ButtonsGroup from '../common/ButtonsGroup';
import SelectComponent from '../common/Select';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const keyToRangeToName = {
  currentValue: {
    week: `Текущая неделя`,
    month: `Текущий месяц`,
    year: `Текущий год`,
  },
  previousValue: {
    week: `Прошлая неделя`,
    month: `Прошлый месяц`,
    year: `Прошлый год`,
  },
};

const rangeToDataName = {
  week: `weeklyData`,
  month: `monthlyData`,
  year: `annuallyData`,
};

const selectByOptions = [
  { value: `delivered`, label: `По доставленным` },
  { value: `undelivered`, label: `По отгруженным` },
];

const Wrapper = styled(ResponsiveContainer)`
  .recharts-wrapper .recharts-cartesian-grid-vertical line:first-of-type,
  .recharts-wrapper .recharts-cartesian-grid-vertical line:last-of-type {
    stroke-opacity: 0;
  }

  .recharts-tooltip-item {
    padding: 0 !important;
    margin-top: 2px;
  }
`;

const CardWrapper = styled(`div`)`
  .header-title {
    display: flex;
    height: 26px;
    align-items: center;
    white-space: nowrap;

    .switch-wrapper {
      display: flex;
      font-size: 13px;
      flex-wrap: nowrap;
      align-items: center;
    }

    .react-select-container {
      min-width: 215px;
      transform: scale(0.7);
      transform-origin: center;
      z-index: 1;
      height: 36px;

      .react-select__single-value {
        font-size: 18px;
        font-weight: normal;
      }
    }
  }
`;

const SalesDynamicsChart = (props) => {
  const {
    salesDynamicDelivered,
    salesDynamicUndelivered,
    keyToLabel,
    formatNumber,
    currencySymbol,
  } = props;

  const { t } = useAutoTranslation();

  const selectByToData = { delivered: salesDynamicDelivered, undelivered: salesDynamicUndelivered };

  const [range, setRange] = useState(`week`);
  const [selectBy, setSelectBy] = useState(selectByOptions[0]);

  const {
    week,
    previousWeek,
    previousWeekDiffDirection,
    previousMonth,
    previousMonthDiffDirection,
  } = selectByToData[selectBy.value];

  console.log('selectByToData', selectByToData);

  return (
    <div className="col-xl-4">
      <CardWrapper className="card">
        <div className="card-body">
          <div className="float-right d-none d-md-inline-block">
            <ButtonsGroup
              className="mb-2"
              data={[
                { value: 'week', label: t('Неделя') },
                { value: 'month', label: t('Месяц') },
                { value: 'year', label: t('Год') },
              ]}
              currentValue={range}
              onChange={setRange}
            />
          </div>
          <h4 className="header-title">
            {t('Динамика продаж')}
            <SelectComponent
              options={selectByOptions}
              value={selectBy}
              onChange={(value) => setSelectBy(value)}
            />
          </h4>
          <div className="row mt-4 text-center">
            <div className="col-4">
              <p className="text-muted font-14 mb-1 text-truncate">{t('Текущая неделя')}</p>
              <h4>{`${formatNumber(week.toLocaleString())} ${currencySymbol}`}</h4>
            </div>
            <div className="col-4">
              <p className="text-muted font-14 mb-1 text-truncate">{t('Прошлая неделя')}</p>
              <h4>
                <i
                  className={`${
                    previousWeekDiffDirection === `UP`
                      ? `fe-arrow-up text-success`
                      : `fe-arrow-down text-danger`
                  } mr-1`}
                />
                {`${formatNumber(previousWeek.toLocaleString())} ${currencySymbol}`}
              </h4>
            </div>
            <div className="col-4">
              <p className="text-muted font-14 mb-1 text-truncate">{t('Прошлый месяц')}</p>
              <h4>
                <i
                  className={`${
                    previousMonthDiffDirection === `UP`
                      ? `fe-arrow-up text-success`
                      : `fe-arrow-down text-danger`
                  } mr-1`}
                />
                {`${formatNumber(previousMonth.toLocaleString())} ${currencySymbol}`}
              </h4>
            </div>
          </div>
          <div className="mt-3">
            <Wrapper width="100%" height={300}>
              <AreaChart
                data={selectByToData[selectBy.value][rangeToDataName[range]]}
                margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="point"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => (value < 10 ? `0${value}` : value)}
                />
                <YAxis
                  width={45}
                  domain={[
                    (dataMin) => Math.round(dataMin * 0.6),
                    (dataMax) => Math.round(dataMax * 1.15),
                  ]}
                  tickCount={7}
                  tickLine={false}
                  strokeWidth={1}
                  tickFormatter={(value) => formatNumber(value)}
                  axisLine={{ stroke: `#c3c3c3` }}
                  tick={{ fill: `#98a6ad` }}
                />
                <CartesianGrid horizontal={false} stroke="#f5f5f5" strokeWidth={1} active={false} />
                <Tooltip
                  cursor={false}
                  formatter={(value, key) => [
                    `${value} ${currencySymbol}`,
                    keyToRangeToName[key][range],
                  ]}
                  labelFormatter={(key) => keyToLabel[key]}
                  contentStyle={{
                    border: `none`,
                    boxShadow: `0 1px 4px 0 rgba(0, 0, 0, 0.1)`,
                    borderRadius: `0.25rem`,
                    opacity: `0.95`,
                    fontWeight: 500,
                    backdropFilter: `blur(2px)`,
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 5 }}
                />
                <Area
                  type="monotone"
                  dataKey="previousValue"
                  stroke="#7e57c2"
                  strokeWidth={3}
                  fillOpacity={0}
                  strokeDasharray="5"
                  dot={{
                    stroke: `#7e57c2`,
                    strokeWidth: 1,
                    strokeDasharray: 0,
                  }}
                  activeDot={false}
                />
                <Area
                  type="monotone"
                  dataKey="currentValue"
                  stroke="#4fc6e1"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="rgba(79, 198, 225, 0.3)"
                  dot={{
                    stroke: `#4fc6e1`,
                    strokeWidth: 1,
                    strokeDasharray: 0,
                  }}
                  activeDot={false}
                />
              </AreaChart>
            </Wrapper>
          </div>
        </div>
      </CardWrapper>
    </div>
  );
};

export default SalesDynamicsChart;
